import clsx from "clsx";
import { get } from "lodash-es";
import Delta from "quill-delta";
import { FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { Doc } from "sharedb/lib/client";

import type { PresenceViewShortType } from "../../../../shared/types/sharedb";
import type { PathType, WorksheetType } from "../../../../shared/types/worksheet";

import {
  ProseMirrorEditorInstance,
  type ProseMirrorEditorInstanceOptions,
} from "./prosemirror/editor-instance";

import { UserType } from "components/server-types";
import { useCapabilities } from "store/capability-hooks";
import { USER_INPUT_EVENT_CATEGORY, addTrackingToEvent, getPageFieldProps } from "utils/amplitude";

export interface ProseMirrorPortalProps {
  containerElement: HTMLElement | null; // portal render target
  className?: string;
  showPlaceholder: boolean;
  placeHolderText: string;
  readOnly: boolean;
  doc: Doc<WorksheetType>; // ShareDB document
  path: PathType; // path to the field's data specifically
  fieldPath?: PathType; // path to entire field definition (e.g. to read metadata like maxLength)
  userId?: string | number; // for ShareDB presence
  presenceViewType?: PresenceViewShortType;
  users: Record<string, UserType>; // section user map for presence cursor labels
  eventName?: string;
  pageId: string; // for error and debug logs
  embedded?: boolean; // renders inside another component
  create?: boolean; // create doc[path] if it doesn't exist yet
  characterCounter: "NONE" | "READ" | "EDIT";
  maxLength?: number | null;
  isAuthoringPage?: boolean;
  onEmbedURLFromPaste?: (url: string) => void; // called when a pasted URL is detected
  onEmbedURLFromLink?: (url: string) => void; // called when a link is added via toolbar; block-level fields only
}

/**
 * Renders the editor UI through a React portal into the container element provided by the parent.
 * A portal is used because ProseMirror manages its own DOM directly, which would conflict with
 * React's reconciliation if they shared the same subtree.
 */
export const ProseMirrorPortal: FC<ProseMirrorPortalProps> = ({
  containerElement,
  className = "",
  readOnly,
  doc,
  path,
  fieldPath,
  userId,
  presenceViewType,
  users,
  eventName,
  pageId,
  embedded = false,
  create = false,
  showPlaceholder = false,
  placeHolderText = "",
  characterCounter,
  maxLength,
  isAuthoringPage,
  onEmbedURLFromPaste,
  onEmbedURLFromLink,
}) => {
  const rootElementRef = useRef<HTMLDivElement | null>(null);
  const editorInstanceRef = useRef<ProseMirrorEditorInstance | null>(null);
  const { canAuthorSection } = useCapabilities();
  const hasContainer = containerElement !== null;

  useEffect(() => {
    if (!containerElement || !rootElementRef.current) return;

    const options: ProseMirrorEditorInstanceOptions = {
      doc,
      path,
      readOnly,
      userId,
      canAuthorSection,
      presenceViewType,
      users,
      embedded,
      create,
      pageId,
      showPlaceholder,
      placeHolderText,
      characterCounter,
      fieldPath,
      maxLength,
      isAuthoringPage,
      onEmbedURLFromPaste,
      onEmbedURLFromLink,
    };
    const instance = new ProseMirrorEditorInstance(rootElementRef.current, options);
    editorInstanceRef.current = instance;

    const editorElement = rootElementRef.current;
    const editorWrapper = editorElement.closest(".prosemirror-editor-wrapper");
    const trackedFocus = addTrackingToEvent({
      eventName: eventName || "Free response block selected",
      eventProperties: { ...getPageFieldProps(pageId, path), ...USER_INPUT_EVENT_CATEGORY },
    });

    // The browser can natively focus a contenteditable element for clicks that land
    // outside its bounding box, with no JS involvement. We detect this by tracking
    // whether the preceding user gesture landed inside the editor wrapper. The reset
    // runs in setTimeout(0) so the flag only gates the synchronous focusin that may
    // follow this gesture, without affecting later keyboard or programmatic focus.
    let shouldAcceptFocus = true;
    const markGestureForFocus = (inWrapper: boolean) => {
      shouldAcceptFocus = inWrapper;
      setTimeout(() => {
        shouldAcceptFocus = true;
      }, 0);
    };

    const handleDocMouseDown = (e: MouseEvent) => {
      const inWrapper = !!editorWrapper?.contains(e.target as Node);
      markGestureForFocus(inWrapper);
    };

    // iOS workaround: with viewport zoom enabled (`maximum-scale=3.0`), the synthetic
    // mousedown/focusin/click chain gets dropped on the first tap into a contenteditable
    // that was just blurred. Without this handler, refocusing the editor would require two taps.
    // `pointerup` is still dispatched and counts as a trusted user gesture, so we use it
    // to mark the gesture and explicitly focus the editor at the tap point.
    const handleDocPointerUp = (e: PointerEvent) => {
      if (e.pointerType !== "touch" && e.pointerType !== "pen") return;

      const inWrapper = !!editorWrapper?.contains(e.target as Node);
      markGestureForFocus(inWrapper);
      if (!inWrapper) return;

      // Skip when already focused: the natural mouse chain does fire here and will
      // place the cursor. Running focusAtCoords too would dispatch a redundant selection.
      const alreadyFocused = !!editorWrapper?.contains(document.activeElement);
      if (alreadyFocused) return;

      instance.focusAtCoords(e.clientX, e.clientY);
      instance.scrollCursorAboveKeyboard();
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (!shouldAcceptFocus) {
        (document.activeElement as HTMLElement | null)?.blur();
        return;
      }

      const relatedTarget = e.relatedTarget as Node | null;
      if (!relatedTarget || !editorWrapper?.contains(relatedTarget)) {
        instance.resetToolbarScroll();
      }

      trackedFocus(e);
    };

    const handleFocusOut = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as Node | null;
      if (!relatedTarget || !editorWrapper?.contains(relatedTarget)) {
        instance.resetToolbarPanel();
      }
    };

    const handleToolbarMouseDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest(".rich-text-toolbar")) {
        e.preventDefault(); // prevents the editor from losing focus/selection on toolbar clicks
      }
    };

    const handleToolbarEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && (e.target as HTMLElement).closest(".rich-text-toolbar")) {
        e.preventDefault();
        if (!instance.dismissToolbarPanel()) {
          editorElement.querySelector<HTMLElement>(".ProseMirror")?.focus();
        }
      }
    };

    document.addEventListener("mousedown", handleDocMouseDown, true);
    document.addEventListener("pointerup", handleDocPointerUp, true);
    editorElement.addEventListener("focusin", handleFocusIn);
    editorElement.addEventListener("focusout", handleFocusOut);
    editorElement.addEventListener("mousedown", handleToolbarMouseDown);
    editorElement.addEventListener("keydown", handleToolbarEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocMouseDown, true);
      document.removeEventListener("pointerup", handleDocPointerUp, true);
      editorElement.removeEventListener("focusin", handleFocusIn);
      editorElement.removeEventListener("focusout", handleFocusOut);
      editorElement.removeEventListener("mousedown", handleToolbarMouseDown);
      editorElement.removeEventListener("keydown", handleToolbarEscape);
      instance.destroy();
      editorInstanceRef.current = null;
    };
    // We should take care to avoid recreating the editor unnecessarily.
    // Dependencies not included: containerElement, doc, path, fieldPath, userId, users, pageId,
    // eventName, className, embedded, create, placeHolderText, characterCounter, maxLength,
    // isAuthoringPage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContainer, readOnly, presenceViewType, showPlaceholder, canAuthorSection]);

  // Timeline view replaces doc between renders, so we pull the new content here
  // Also, ShareDB's automatic error recovery will re-fetch the doc data entirely on error,
  // which this file needs to react to by detecting changes in doc.data.
  useEffect(() => {
    if (editorInstanceRef.current) {
      const data = get(doc.data, path) as Delta;
      editorInstanceRef.current.syncContentsFromDoc(data, path);
    }
  }, [doc, path, doc?.data]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setFieldPath(fieldPath);
    }
  }, [fieldPath]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setIsAuthoringPage(isAuthoringPage ?? false);
    }
  }, [isAuthoringPage]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateCharacterCountLimit(maxLength || null);
    }
  }, [maxLength]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateCharacterCountMode(characterCounter);
    }
  }, [characterCounter]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateOnEmbedURLFromPaste(onEmbedURLFromPaste);
    }
  }, [onEmbedURLFromPaste]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateOnEmbedURLFromLink(onEmbedURLFromLink);
    }
  }, [onEmbedURLFromLink]);

  if (!containerElement) {
    return null;
  }

  return createPortal(
    <div
      className={clsx(
        "prosemirror-editor-wrapper relative flex flex-col rounded-md border border-white",
        className,
        {
          "textfield-disabled": readOnly,
          embedded,
        },
      )}
    >
      {/* This div is where ProseMirror attaches the EditorView — do not render React children here. */}
      <div
        className="prosemirror-editor flex flex-1 flex-col text-black @container [outline:none]"
        ref={rootElementRef}
      />
    </div>,
    containerElement,
  );
};
